import { useEffect, useState } from 'react';
import { SESSION_CONTEXT_MODES } from '@/types/prioritization/workflow';
import { ChevronDown, ChevronUp, GitBranch, LayoutTemplate } from 'lucide-react';
import { useWorkflowStore } from '@/stores/prioritization/workflowStore';
import type { DegapCycle } from '@/types/prioritization/degapCycle';
import type { CycleTemplateSeed } from '@/types/prioritization/cycleTemplate';
import type { Track } from '@/types/prioritization/track';
import type { EffectiveWorkflowConfig, Workflow } from '@/types/prioritization/workflow';

interface WorkflowPresetSummaryProps {
  cycle: DegapCycle | null;
  track?: Track | null;
  className?: string;
}

const SESSION_MODE_LABELS: Record<string, string> = {
  cycle_linked: 'Cycle linked',
  app_linked: 'App linked',
  isolated: 'Isolated',
};

export function WorkflowPresetSummary({ cycle, track = null, className = '' }: WorkflowPresetSummaryProps) {
  const getEffectiveWorkflowForCycle = useWorkflowStore((state) => state.getEffectiveWorkflowForCycle);
  const getEffectiveConfigForCycle = useWorkflowStore((state) => state.getEffectiveConfigForCycle);
  const [effectiveWorkflow, setEffectiveWorkflow] = useState<Workflow | null>(null);
  const [effectiveConfig, setEffectiveConfig] = useState<EffectiveWorkflowConfig | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!cycle) return;
    let cancelled = false;

    Promise.all([
      getEffectiveWorkflowForCycle(cycle),
      getEffectiveConfigForCycle(cycle),
    ]).then(([workflow, config]) => {
      if (cancelled) return;
      setEffectiveWorkflow(workflow);
      setEffectiveConfig(config);
    }).catch((error) => {
      if (!cancelled) {
        console.warn('[WorkflowPresetSummary] Failed to resolve workflow summary:', error);
      }
    });

    return () => { cancelled = true; };
  }, [cycle, getEffectiveWorkflowForCycle, getEffectiveConfigForCycle]);

  // What the cycle was seeded with when it was made (degap_cycles.template_seed,
  // mig 1040) — not the template's current contents, which never reach a cycle.
  const seed: CycleTemplateSeed | null = cycle?.templateSeed ?? null;
  const appliedTemplate = seed
    ? { name: seed.template_name ?? (seed.source === 'workflow_configuration' ? 'Workflow presets' : 'Template') }
    : null;
  const seedAttributes = Array.isArray(seed?.attributes) ? seed.attributes : [];

  const workflowConfig = effectiveWorkflow?.configuration;
  const defaultTiming = workflowConfig?.default_timing_mode ?? 'live';
  const defaultEntry = workflowConfig?.default_entry_mode ?? 'facilitator';
  const defaultFacilitator = workflowConfig?.default_facilitator_id ?? null;
  const resultResolution = effectiveConfig?.resultResolution ?? 'latest_published';
  const catchballEnabled = effectiveConfig?.catchballEnabled ?? true;
  const allowedModes = effectiveConfig?.allowedSessionModes ?? SESSION_CONTEXT_MODES;

  // Template counts — from the seed the cycle took, not from workflow config
  const attributePresetCount = seedAttributes.length;
  const participantPresetCount = Array.isArray(seed?.participants) ? seed.participants.length : 0;
  const groupPresetCount = Array.isArray(seed?.groups) ? seed.groups.length : 0;
  const scalePresetCount = seedAttributes.filter((a) => !!a?.scaleId).length;
  const rangePresetCount = seedAttributes.filter((a) => !!a?.defaultRange).length;

  if (!cycle) return null;

  return (
    <div className={` border border-[var(--slate)] bg-[var(--slate-dim)]/60 ${className}`}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        {/* Workflow identity — collapsed view shows only name + template */}
        <div className="flex min-w-0 items-center gap-2">
          <GitBranch className="h-4 w-4 text-[var(--text-ter)]" />
          <span className="text-sm font-semibold text-[var(--text-ter)]">Workflow</span>
          <span className="truncate text-sm text-[var(--text-ter)]">
            <strong>{effectiveWorkflow?.name ?? 'Canonical fallback'}</strong>
          </span>
        </div>

        {/* Template badge — only shown when a template was applied */}
        {appliedTemplate && (
          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--green)] bg-[var(--green-dim)] px-2 py-0.5 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
            <LayoutTemplate className="h-3 w-3" />
            {appliedTemplate.name}
          </span>
        )}

        <button
          type="button"
          onClick={() => setShowDetails((current) => !current)}
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
        >
          {showDetails ? 'Less' : 'Details'}
          {showDetails ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      {showDetails && (
        <div className="border-t border-[var(--slate)] px-4 py-3 text-sm">
          {effectiveWorkflow?.description && (
            <p className="mb-3 text-xs text-[var(--text-ter)]">{effectiveWorkflow.description}</p>
          )}

          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
            {/* Session cascade defaults */}
            <div className="border border-[var(--slate)] bg-[var(--bg-raised)]/80 p-2.5">
              <div className="text-[11px] uppercase tracking-wide text-[var(--text-ter)]">Session defaults</div>
              <div className="mt-1 text-[var(--text-pri)]">
                Timing: <strong>{defaultTiming}</strong><br />
                Entry: <strong>{defaultEntry}</strong><br />
                Facilitator: <strong>{defaultFacilitator ?? 'Unset'}</strong>
              </div>
            </div>

            {/* Orchestration policy */}
            <div className="border border-[var(--slate)] bg-[var(--bg-raised)]/80 p-2.5">
              <div className="text-[11px] uppercase tracking-wide text-[var(--text-ter)]">Behavior</div>
              <div className="mt-1 text-[var(--text-pri)]">
                Result resolution: <strong>{resultResolution.replace(/_/g, ' ')}</strong><br />
                Catchball: <strong>{catchballEnabled ? 'enabled' : 'disabled'}</strong>
              </div>
            </div>

            {/* Allowed session modes */}
            <div className="border border-[var(--slate)] bg-[var(--bg-raised)]/80 p-2.5">
              <div className="text-[11px] uppercase tracking-wide text-[var(--text-ter)]">Allowed session modes</div>
              <div className="mt-1 text-[var(--text-pri)]">
                {allowedModes.map((mode) => SESSION_MODE_LABELS[mode] ?? mode).join(', ')}
              </div>
            </div>

            {/* Applied template — the seed recorded on the cycle (mig 1040) */}
            <div className="border border-[var(--slate)] bg-[var(--bg-raised)]/80 p-2.5">
              <div className="text-[11px] uppercase tracking-wide text-[var(--text-ter)]">Applied template</div>
              <div className="mt-1 text-[var(--text-pri)]">
                {appliedTemplate ? (
                  <>
                    <strong className="flex items-center gap-1">
                      <LayoutTemplate className="h-3 w-3 text-[var(--green)]" />
                      {appliedTemplate.name}
                    </strong>
                    <span className="text-xs text-[var(--text-ter)] block mt-0.5">Applied once at cycle creation</span>
                    {attributePresetCount > 0 && (
                      <span className="text-xs block">
                        {attributePresetCount} attribute{attributePresetCount !== 1 ? 's' : ''}
                        {scalePresetCount > 0 && `, ${scalePresetCount} with scale`}
                        {rangePresetCount > 0 && `, ${rangePresetCount} with range`}
                      </span>
                    )}
                    {participantPresetCount > 0 && (
                      <span className="text-xs block">{participantPresetCount} participant{participantPresetCount !== 1 ? 's' : ''}</span>
                    )}
                    {groupPresetCount > 0 && (
                      <span className="text-xs block">{groupPresetCount} group{groupPresetCount !== 1 ? 's' : ''}</span>
                    )}
                  </>
                ) : (
                  <span className="text-[var(--text-ter)] italic text-xs">No template applied at creation</span>
                )}
              </div>
            </div>
          </div>

          {track && (
            <p className="mt-2 text-xs text-[var(--text-ter)]">
              Template attributes seeded at cycle creation. Session values inherit through cycle and track unless locally overridden.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default WorkflowPresetSummary;
